import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from './Navbar.js';
import './MedicalTravellerForm.css';
import header from './assets/images/islandhospital30anniversary.jpg';
import {
    FaHospital,
    FaUser,
    FaPlane,
    FaHotel,
    FaCalendarAlt,
    FaHeartbeat,
    FaWallet,
    FaBuilding,
    FaClipboardList,
} from 'react-icons/fa';

const SURVEY_ID = '68c0a1b2ece92761170b9601';
const LANGUAGE_PATH = '/f/TWVkaWNhbFRyYXZlbGxlckZvcm0vbGFuZ3VhZ2U=';
const DEFAULT_AGENT_ID = 'MedicalTraveller';

/** Survey open window in Malaysia time (Asia/Kuala_Lumpur, UTC+8). */
const SURVEY_START_MYT = new Date('2026-09-01T00:00:00+08:00');
const SURVEY_END_MYT = new Date('2026-10-31T23:59:59.999+08:00');
const isMedicalTravellerSurveyAvailable = (now = new Date()) =>
    now >= SURVEY_START_MYT && now <= SURVEY_END_MYT;

const SECTION_ICONS = [
    FaHospital,      // Hospital Selection
    FaUser,          // Section A: Respondent Profile
    FaPlane,         // Section B: Visit Information
    FaHotel,         // Section C: Accommodation
    FaCalendarAlt,   // Section D: Length of Stay
    FaHeartbeat,     // Section E: Medical Journey
    FaWallet,        // Section F: Travel & Spending
    FaBuilding,      // Section G: Future Accommodation Demand
];

// Use same host as the page so phone/LAN (e.g. 10.10.40.23) works, not the device's localhost
const API_SERVER = (() => {
    const configured = (process.env.REACT_APP_AI_FORM_SERVER || '').replace(/\/$/, '');
    if (configured && /islandhospital\.com/i.test(configured)) {
        return configured;
    }
    if (typeof window !== 'undefined' && window.location?.hostname) {
        return `${window.location.protocol}//${window.location.hostname}:4454`;
    }
    return configured || 'http://localhost:4454';
})();

const isOtherOption = (value) => {
    if (!value) return false;
    const normalized = String(value).trim().toLowerCase();
    return normalized === 'other' || normalized === 'lainnya';
};

const isOtherFollowUpQuestion = (question) => {
    const en = question?.questions?.en || '';
    const id = question?.questions?.id || '';
    return /If Other/i.test(en) || /Jika Lainnya/i.test(id);
};

const getQuestionCode = (question) => {
    const en = question?.questions?.en || '';
    const match = en.match(/^Q(\d+)([a-e])?\./i);
    if (!match) return null;
    return {
        number: match[1],
        letter: match[2] ? match[2].toLowerCase() : null,
        isFollowUp: Boolean(match[2]),
    };
};

const isQ5DependentQuestion = (question) => {
    const code = getQuestionCode(question);
    return code && !code.letter && (code.number === '6' || code.number === '7');
};

const isQ12DependentQuestion = (question) => {
    const code = getQuestionCode(question);
    return code && !code.letter && code.number === '13';
};

const ACCOMMODATION_PAID_STAY_OPTIONS = [
    'hotel',
    'serviced apartment',
    'apartemen berlayanan (serviced apartment)',
    'homestay / airbnb',
];

const isPaidStayAccommodation = (value) => {
    if (!value) return false;
    const normalized = String(value).trim().toLowerCase();
    return ACCOMMODATION_PAID_STAY_OPTIONS.includes(normalized);
};

const isNotFirstVisit = (value) => {
    if (!value) return false;
    const normalized = String(value).trim().toLowerCase();
    return normalized === 'no' || normalized === 'tidak';
};

const findQuestionByNumber = (questionList, number) =>
    questionList.find((q) => {
        const code = getQuestionCode(q);
        return code && !code.letter && code.number === String(number);
    }) || null;

const findParentQuestion = (question, questionList) => {
    if (!isOtherFollowUpQuestion(question)) return null;
    const code = getQuestionCode(question);
    if (!code?.letter) return null;
    return findQuestionByNumber(questionList, code.number);
};

const isOtherSelected = (parentQuestion, formValues, language) => {
    if (!parentQuestion) return false;
    const parentKey = parentQuestion.questions?.[language];
    const value = formValues[parentKey];
    if (Array.isArray(value)) {
        return value.some(isOtherOption);
    }
    return isOtherOption(value);
};

const isQ5PaidStaySelected = (questionList, formValues, language) => {
    const q5 = findQuestionByNumber(questionList, 5);
    if (!q5) return false;
    const q5Key = q5.questions?.[language];
    return isPaidStayAccommodation(formValues[q5Key]);
};

const isQ12NotFirstVisitSelected = (questionList, formValues, language) => {
    const q12 = findQuestionByNumber(questionList, 12);
    if (!q12) return false;
    const q12Key = q12.questions?.[language];
    return isNotFirstVisit(formValues[q12Key]);
};

const shouldShowQuestion = (question, questionList, formValues, language) => {
    if (isQ5DependentQuestion(question)) {
        return isQ5PaidStaySelected(questionList, formValues, language);
    }
    if (isQ12DependentQuestion(question)) {
        return isQ12NotFirstVisitSelected(questionList, formValues, language);
    }
    if (!isOtherFollowUpQuestion(question)) return true;
    const parent = findParentQuestion(question, questionList);
    return isOtherSelected(parent, formValues, language);
};

const isQuestionRequired = (question, questionList, formValues, language) => {
    if (isQ5DependentQuestion(question)) {
        return isQ5PaidStaySelected(questionList, formValues, language);
    }
    if (isQ12DependentQuestion(question)) {
        return isQ12NotFirstVisitSelected(questionList, formValues, language);
    }
    return Boolean(question.required);
};

const isNumericOnlyQuestion = (question) => {
    const en = question?.questions?.en || '';
    return /^Q10\./i.test(en) || /^Q14[a-e]\./i.test(en);
};

const toDigitsOnly = (value) => String(value ?? '').replace(/\D/g, '');

const MT_DEVICE_ID_KEY = 'mt_survey_device_id';

const getOrCreateDeviceId = () => {
    try {
        let id = localStorage.getItem(MT_DEVICE_ID_KEY);
        if (!id) {
            id = (window.crypto?.randomUUID?.()) ||
                `mt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
            localStorage.setItem(MT_DEVICE_ID_KEY, id);
        }
        return id;
    } catch (error) {
        return `mt-temp-${Date.now()}`;
    }
};

// Silent browser signals only — serial number / Wi-Fi SSID are not available to web pages
const collectClientInfo = () => ({
    deviceId: getOrCreateDeviceId(),
    userAgent: navigator.userAgent || '',
    platform: navigator.platform || '',
    language: navigator.language || '',
    languages: Array.isArray(navigator.languages) ? navigator.languages.join(',') : '',
    screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    viewport: `${window.innerWidth || 0}x${window.innerHeight || 0}`,
    colorDepth: window.screen?.colorDepth || null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    timezoneOffset: new Date().getTimezoneOffset(),
    touchPoints: navigator.maxTouchPoints || 0,
    cookieEnabled: navigator.cookieEnabled,
    hardwareConcurrency: navigator.hardwareConcurrency || null,
    vendor: navigator.vendor || '',
});

function MedicalTravellerForm() {
    const moment = require('moment-timezone');
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { language = 'en', agentId: agentIdFromState } = location.state || {};
    const agentIdParam = searchParams.get('AgentId') || searchParams.get('agentId') || '';

    const [questionList, setQuestionList] = useState([]);
    const [surveyID, setSurveyID] = useState('');
    const [formValues, setFormValues] = useState({});
    const [loading, setLoading] = useState(true);
    const [resolvedAgentId, setResolvedAgentId] = useState(agentIdFromState || DEFAULT_AGENT_ID);
    const [agentStatus, setAgentStatus] = useState(agentIdFromState ? 'ready' : 'loading');

    const languageLink = agentIdParam
        ? `${LANGUAGE_PATH}?AgentId=${encodeURIComponent(agentIdParam)}`
        : LANGUAGE_PATH;

    const uiText = {
        en: {
            title: 'Medical Traveller Accommodation & Travel Survey',
            subtitle: '2026 Update',
            requiredNote: '* is a required field',
            previous: 'Previous',
            submit: 'Submit',
            confirm: 'Are you sure you want to submit the form?',
            success: 'Success.',
            fail: 'Failed to submit form, please try again.',
            checkboxAlert: 'Please select at least one option for required questions.',
            loading: 'Loading survey...',
            noAgent: 'No such agent',
            agentError: 'Unable to validate agent',
            unavailableTitle: 'This survey is unavailable',
            unavailableText:
                'This survey is only available until 31 October 2026 (Malaysia time).',
        },
        id: {
            title: 'Survei Akomodasi & Perjalanan Wisata Medis',
            subtitle: 'Pembaruan 2026',
            requiredNote: '* wajib diisi',
            previous: 'Sebelumnya',
            submit: 'Kirim',
            confirm: 'Apakah Anda yakin ingin mengirim formulir ini?',
            success: 'Berhasil.',
            fail: 'Gagal mengirim formulir, silakan coba lagi.',
            checkboxAlert: 'Harap pilih setidaknya satu opsi untuk pertanyaan wajib.',
            loading: 'Memuat survei...',
            noAgent: 'Agen tidak ditemukan',
            agentError: 'Gagal memvalidasi agen',
            unavailableTitle: 'Survei ini tidak tersedia',
            unavailableText:
                'Survei ini hanya tersedia hingga 31 Oktober 2026 (waktu Malaysia).',
        }
    };

    const t = uiText[language] || uiText.en;

    useEffect(() => {
        if (!location.state?.language) {
            navigate(languageLink, { replace: true });
        }
    }, [location.state, navigate, languageLink]);

    useEffect(() => {
        if (!isMedicalTravellerSurveyAvailable()) {
            return;
        }

        const validateAgent = async () => {
            // Already validated on language page and passed via state
            if (agentIdFromState && !agentIdParam) {
                setResolvedAgentId(agentIdFromState);
                setAgentStatus('ready');
                return;
            }

            try {
                const response = await fetch(`${API_SERVER}/api/validateMedicalTravellerAgent`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        agentId: agentIdParam || null,
                        survey_id: SURVEY_ID,
                    }),
                });
                const data = await response.json();

                if (!data.success) {
                    window.alert(t.agentError);
                    setAgentStatus('error');
                    return;
                }

                if (!data.valid) {
                    window.alert(t.noAgent);
                    setAgentStatus('invalid');
                    return;
                }

                setResolvedAgentId(data.agent?.agentId || DEFAULT_AGENT_ID);
                setAgentStatus('ready');
            } catch (error) {
                console.log('Error validating agent:', error);
                window.alert(t.agentError);
                setAgentStatus('error');
            }
        };

        validateAgent();
    }, [agentIdParam, agentIdFromState, t.agentError, t.noAgent]);

    useEffect(() => {
        if (!isMedicalTravellerSurveyAvailable()) {
            setLoading(false);
            return;
        }

        const fetchQuestion = async () => {
            try {
                const response = await fetch(
                    `${API_SERVER}/api/getQuestion?_=${new Date().getTime()}`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ survey_id: SURVEY_ID }),
                    }
                );

                const jsonData = await response.json();
                setSurveyID(jsonData[0]?.survey_id || SURVEY_ID);
                setQuestionList(jsonData[0]?.details || []);
            } catch (error) {
                console.log('Error fetching questions:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchQuestion();
    }, []);

    useEffect(() => {
        const initialValues = {};
        questionList.forEach((question) => {
            const key = question.questions?.[language];
            if (!key) return;

            if (question.qtype === 'checkbox_multiple') {
                initialValues[key] = [];
            } else {
                initialValues[key] = '';
            }
        });
        setFormValues(initialValues);
    }, [questionList, language]);

    const handleFormChange = (e, question) => {
        const { name, value } = e.target;
        const nextValue = isNumericOnlyQuestion(question) ? toDigitsOnly(value) : (value ?? '');
        setFormValues((prev) => ({
            ...prev,
            [name]: nextValue,
        }));
    };

    const clearFollowUpForParent = (parentQuestion, nextValues) => {
        const parentCode = getQuestionCode(parentQuestion);
        if (!parentCode || parentCode.letter) return nextValues;

        const followUp = questionList.find((q) => {
            if (!isOtherFollowUpQuestion(q)) return false;
            const code = getQuestionCode(q);
            return code?.letter && code.number === parentCode.number;
        });
        if (!followUp) return nextValues;

        const followUpKey = followUp.questions?.[language];
        if (!followUpKey) return nextValues;

        return {
            ...nextValues,
            [followUpKey]: '',
        };
    };

    const clearQ6Q7IfNeeded = (nextValues) => {
        if (isQ5PaidStaySelected(questionList, nextValues, language)) {
            return nextValues;
        }

        const cleared = { ...nextValues };
        ['6', '7'].forEach((num) => {
            const q = findQuestionByNumber(questionList, num);
            const key = q?.questions?.[language];
            if (key) cleared[key] = '';
        });
        return cleared;
    };

    const clearQ13IfNeeded = (nextValues) => {
        if (isQ12NotFirstVisitSelected(questionList, nextValues, language)) {
            return nextValues;
        }

        const cleared = { ...nextValues };
        const q13 = findQuestionByNumber(questionList, 13);
        const key = q13?.questions?.[language];
        if (key) cleared[key] = '';
        return cleared;
    };

    const handleOptionChange = (question, option, checked) => {
        const qKey = question.questions?.[language];
        if (!qKey) return;

        setFormValues((prev) => {
            let nextValues = { ...prev };

            if (question.qtype === 'checkbox_single') {
                nextValues[qKey] = checked ? option[language] : '';
            } else {
                const checkedOptions = prev[qKey] || [];
                nextValues[qKey] = checked
                    ? [...checkedOptions, option[language]]
                    : checkedOptions.filter((opt) => opt !== option[language]);
            }

            if (!isOtherSelected(question, nextValues, language)) {
                nextValues = clearFollowUpForParent(question, nextValues);
            }

            const parentCode = getQuestionCode(question);
            if (parentCode && !parentCode.letter && parentCode.number === '5') {
                nextValues = clearQ6Q7IfNeeded(nextValues);
            }
            if (parentCode && !parentCode.letter && parentCode.number === '12') {
                nextValues = clearQ13IfNeeded(nextValues);
            }

            return nextValues;
        });
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        const visibleQuestions = questionList.filter((question) =>
            shouldShowQuestion(question, questionList, formValues, language)
        );

        const invalidCheckboxes = visibleQuestions.some((question) => {
            if (question.qtype === 'checkbox_single' || question.qtype === 'checkbox_multiple') {
                const questionKey = question?.questions?.[language];
                if (!questionKey) return false;

                const selectedValues = formValues[questionKey];
                const required = isQuestionRequired(question, questionList, formValues, language);
                return required && (!selectedValues || selectedValues.length === 0);
            }
            return false;
        });

        if (invalidCheckboxes) {
            alert(t.checkboxAlert);
            return;
        }

        const confirmed = window.confirm(t.confirm);
        if (!confirmed) return;

        // Only submit visible answers; clear hidden conditional fields
        const responsePayload = {};
        questionList.forEach((question) => {
            const key = question.questions?.[language];
            if (!key) return;
            if (shouldShowQuestion(question, questionList, formValues, language)) {
                responsePayload[key] = formValues[key];
            } else if (question.qtype === 'checkbox_multiple') {
                responsePayload[key] = [];
            } else {
                responsePayload[key] = '';
            }
        });

        try {
            const response = await fetch(`${API_SERVER}/api/formSubmit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    survey_id: surveyID,
                    language,
                    response: responsePayload,
                    submissionDate: moment().tz('Asia/Kuala_Lumpur').format(),
                    agentId: resolvedAgentId || DEFAULT_AGENT_ID,
                    clientInfo: collectClientInfo(),
                }),
            });

            const data = await response.json();

            if (data.success) {
                window.alert(t.success);
                window.location.href = '/formSubmit';
            } else {
                window.alert(t.fail);
            }
        } catch (error) {
            console.log('Error submitting form:', error);
            window.alert(t.fail);
        }
    };

    const groupedQuestions = questionList.reduce((acc, question) => {
        const groupName = question.group?.[language] || 'Ungrouped';
        if (!acc[groupName]) acc[groupName] = [];
        acc[groupName].push(question);
        return acc;
    }, {});

    if (!location.state?.language) {
        return null;
    }

    if (!isMedicalTravellerSurveyAvailable()) {
        return (
            <div className="mt-form-page">
                <Navbar currentPage="PickupForm" formLink={languageLink} />
                <div className="mt-form-container">
                    <br /><br />
                    <div className="mt-form-title">
                        <h1>{t.title}</h1>
                        <p style={{ color: '#b00020', marginTop: '16px' }}>
                            {t.unavailableTitle}
                        </p>
                        <p style={{ color: '#5a6f6f', marginTop: '8px' }}>
                            {t.unavailableText}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (agentStatus === 'invalid' || agentStatus === 'error') {
        return (
            <div className="mt-form-page">
                <Navbar currentPage="PickupForm" formLink={languageLink} />
                <div className="mt-form-container">
                    <br /><br />
                    <div className="mt-form-title">
                        <h1>{t.title}</h1>
                        <p style={{ color: '#b00020', marginTop: '16px' }}>
                            {agentStatus === 'invalid' ? t.noAgent : t.agentError}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="mt-form-page">
            <Navbar
                currentPage="PickupForm"
                formLink={languageLink}
            />
            <div className="mt-form-container">
                <br /><br />

                <div className="mt-header-container">
                    <img
                        src={header}
                        alt="Island Hospital 30th Anniversary"
                        className="mt-header-image"
                    />
                </div>

                <div className="mt-form-title">
                    <h1>{t.title}</h1>
                    <p>{t.subtitle}</p>
                </div>

                <form onSubmit={handleFormSubmit}>
                    <span className="mt-required-note">{t.requiredNote}</span>
                    <hr />

                    {loading && <p>{t.loading}</p>}

                    {Object.entries(groupedQuestions).map(([groupName, questions], groupIndex) => {
                        const SectionIcon = SECTION_ICONS[groupIndex] || FaClipboardList;
                        return (
                        <div key={groupIndex} className="mt-section">
                            <div className="mt-section-header">
                                <SectionIcon className="mt-section-icon" aria-hidden="true" />
                                <h3>{groupName}</h3>
                            </div>

                            {questions.map((question, index) => {
                                if (!shouldShowQuestion(question, questionList, formValues, language)) {
                                    return null;
                                }

                                const qKey = question.questions?.[language];
                                const required = isQuestionRequired(
                                    question,
                                    questionList,
                                    formValues,
                                    language
                                );
                                return (
                                    <div key={index} className="mt-question">
                                        <p>
                                            {qKey}{' '}
                                            {required && (
                                                <span className="mt-required-asterisk">*</span>
                                            )}
                                        </p>

                                        {question.qtype === 'text' && (
                                            <input
                                                name={qKey}
                                                value={formValues[qKey] || ''}
                                                onChange={(e) => handleFormChange(e, question)}
                                                required={required}
                                                type={isNumericOnlyQuestion(question) ? 'tel' : 'text'}
                                                inputMode={isNumericOnlyQuestion(question) ? 'numeric' : undefined}
                                                pattern={isNumericOnlyQuestion(question) ? '[0-9]*' : undefined}
                                                onKeyDown={(e) => {
                                                    if (!isNumericOnlyQuestion(question)) return;
                                                    // Allow control keys; block e/E/+/-/. and letters
                                                    if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                            />
                                        )}

                                        {(question.qtype === 'checkbox_single' ||
                                            question.qtype === 'checkbox_multiple') && (
                                            <div className="mt-options">
                                                {(question.options || []).map((option, idx) => (
                                                    <label key={idx}>
                                                        <input
                                                            type="checkbox"
                                                            name={qKey}
                                                            value={option[language]}
                                                            checked={
                                                                question.qtype === 'checkbox_single'
                                                                    ? formValues[qKey] === option[language]
                                                                    : formValues[qKey]?.includes(option[language]) || false
                                                            }
                                                            onChange={(e) =>
                                                                handleOptionChange(question, option, e.target.checked)
                                                            }
                                                        />
                                                        {option[language]}
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        );
                    })}

                    <div className="mt-form-actions">
                        <input
                            type="button"
                            className="mt-submit-btn"
                            value={t.previous}
                            onClick={() => navigate(languageLink)}
                        />
                        <input
                            type="submit"
                            className="mt-submit-btn"
                            value={t.submit}
                            disabled={agentStatus !== 'ready'}
                        />
                    </div>
                </form>
            </div>
        </div>
    );
}

export default MedicalTravellerForm;
